import { isPlatformBrowser } from '@angular/common';
import { inject, Injectable, PLATFORM_ID, signal } from '@angular/core';

/**
 * Marca del pop-up del newsletter (B9 web, W6). Versionada: si algún día cambia
 * el criterio, se sube a `-v2` y el pop-up vuelve a salir una vez. Guarda la fecha
 * ISO en que se mostró, se cerró o el usuario se suscribió. Nunca va a BD.
 */
export const NEWSLETTER_POPUP_STORAGE_KEY = 'ps-newsletter-popup-v1';

/**
 * Estado del pop-up compartido con los formularios: `NewsletterFormComponent`
 * llama a `markSubscribed()` al suscribirse desde CUALQUIER host (los hosts no
 * escuchan `subscribed`), y el pop-up no vuelve a salir.
 *
 * Cada acceso a `localStorage` va en try/catch. Si el storage falla (modo
 * privado, bloqueado por el navegador…), `isMarked()` responde `true`: el pop-up
 * se comporta como si ya se hubiera mostrado y no molesta. En el servidor,
 * también `true`.
 */
@Injectable({ providedIn: 'root' })
export class NewsletterPopupStateService {
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  private readonly _subscribed = signal(false);
  /** El usuario se ha suscrito en esta pestaña desde cualquier formulario. */
  readonly subscribed = this._subscribed.asReadonly();

  /** ¿Ya hay marca? `true` también si no se puede leer el storage o no hay navegador. */
  isMarked(): boolean {
    if (!this.isBrowser) return true;
    try {
      return localStorage.getItem(NEWSLETTER_POPUP_STORAGE_KEY) !== null;
    } catch {
      return true;
    }
  }

  /** Escribe la marca con la fecha actual. `false` si el storage no la acepta. */
  mark(): boolean {
    if (!this.isBrowser) return false;
    try {
      localStorage.setItem(NEWSLETTER_POPUP_STORAGE_KEY, new Date().toISOString());
      return true;
    } catch {
      return false;
    }
  }

  /** Suscripción con éxito desde cualquier formulario: marca y avisa al pop-up. */
  markSubscribed(): void {
    this.mark();
    this._subscribed.set(true);
  }
}
